import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PawPrint, Lock, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (pw1.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-pf-bg flex flex-col p-8">
      <div className="flex items-center gap-2 mb-10">
        <div className="h-9 w-9 rounded-lg bg-pf-primary text-white grid place-items-center">
          <PawPrint size={20} weight="fill" />
        </div>
        <span className="font-display font-extrabold text-lg tracking-tight text-pf-text">Pawfile</span>
      </div>
      <div className="flex-1 flex items-start justify-center">
        <div className="pf-card p-8 w-full max-w-md">
          {done ? (
            <div>
              <div className="h-12 w-12 rounded-full bg-pf-success-bg grid place-items-center mb-4">
                <CheckCircle size={28} weight="fill" className="text-pf-success" />
              </div>
              <h1 className="text-2xl font-display font-bold text-pf-text">Password updated</h1>
              <p className="mt-2 text-sm text-pf-text-secondary">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-display font-bold text-pf-text">Set a new password</h1>
              <p className="mt-1 text-sm text-pf-text-secondary">Choose a new password for your Pawfile account.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="pf-label mb-1.5 block">New password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                    <input
                      data-testid="new-password-input"
                      type="password"
                      required
                      autoFocus
                      className="pf-input pl-9"
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="pf-label mb-1.5 block">Confirm password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                    <input
                      data-testid="confirm-password-input"
                      type="password"
                      required
                      className="pf-input pl-9"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                    />
                  </div>
                </div>
                {err && <div className="text-sm text-pf-danger bg-pf-danger-bg border border-pf-danger/20 rounded-lg px-3 py-2">{err}</div>}
                <button data-testid="reset-submit-btn" disabled={busy || !token} type="submit" className="pf-btn-primary w-full justify-center py-2.5">
                  {busy ? "Updating…" : "Update password"}
                </button>
                {!token && <div className="text-xs text-pf-danger">Missing reset token.</div>}
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-pf-text-secondary hover:text-pf-text">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
